/*
 * Authors: mariushirjanu
 * Created: 15/12/2023
 */
import { HttpDataService } from '@app/shared/http/http-data.service';

import { GA1Service, MESSAGE_ASSET_SAVE_FAILED, MESSAGE_ASSET_SAVED, MESSAGE_ASSET_SAVING, MESSAGE_ASSET_VALIDATION_FAILED, MESSAGE_SOW_RESULT } from '@app/shared/ga1/ga1.service';
import { Injectable } from '@angular/core';
import { IObject } from '@type_definitions/datatypes';

export interface IAssetResponse {
  assets:IAsset[];
  orderId:number;
  orderDate:Date;
  displayShipFieldForBP:boolean;
  displayShipFieldForDistri:boolean;
}

export interface IAsset {
  partNumber:string;
  category:string;
  description:string;
  serialNumber:string;
  assetTag?:string;
  assetDescription?:string;
  user?:string;
  macAddress?:string;
  mac2Address?:string;
  uuid?:string;
  imei?:string;
  parentProductSerialNumber?:string;
  warrantyExpiryDate?:number;
  warrantyActivationNumber?:string;
  leasingContractNumber?:string;
  leasingStartDate?:number;
  leasingTerm?:number;
  leasingEndDate?:number;
  rentalExtension?:number;
  rentalEndDate?:number;
  paymentPeriod?:string;
  beginningOfPayment?:string;
  endOfPayment?:string;
  shippingEstimationDate?:number;
  shippingActualDate?:number;
  arrivalEstimationDate?:number;
  arrivalActualDate?:number;
  oemReferenceNumber:string;
  real:boolean;
  id?:string;
  serialisable:boolean;
  serialNumberRequired:boolean;
  serialNumberMinLength?:number;
  serialNumberMaxLength?:number;
  vendorRegexPattern?:string;
}

export interface IAssetInfoCalculation {
  assetId?:number;
  leasingStartDate:number;
  leasingTerm:number;
  rentalExtension:number;
  leasingEndDate?:number;
  rentalEndDate?:number;
  endOfPayment?:string;
}

export interface ISOWResult {
  SERIAL:boolean;
  CAREPACK:boolean;
}

export enum AssetColumn {
  ACTUAL_DELIVER_DATE_AT_BP = 'actualDeliverDateAtBP',
  ACTUAL_DELIVER_DATE_AT_CUSTO = 'actualDeliverDateAtCusto',
  ACTUAL_DELIVER_DATE_AT_DISTRI = 'actualDeliverDateAtDistri',
  ACTUAL_SHIP_DATE_TO_BP = 'actualShipDateToBP',
  ACTUAL_SHIP_DATE_TO_DISTRI = 'actualShipDateToDistri',
  ACTUAL_SHIP_DATE_TO_CUSTO = 'actualShipDateToCusto',
  ASSET_DESCRIPTION = 'assetDescription',
  ASSET_TAG = 'assetTag',
  BEGINNING_OF_PAYMENT = 'beginningOfPayment',
  CATEGORY = 'category',
  DESCRIPTION = 'description',
  END_OF_PAYMENT = 'endOfPayment',
  ESTIMATE_DELIVER_DATE_AT_BP = 'estimateDeliverDateAtBP',
  ESTIMATE_DELIVER_DATE_AT_CUSTO = 'estimateDeliverDateAtCusto',
  ESTIMATE_DELIVER_DATE_AT_DISTRI = 'estimateDeliverDateAtDistri',
  ESTIMATE_SHIP_DATE_TO_BP = 'estimateShipDateToBP',
  ESTIMATE_SHIP_DATE_TO_CUSTO = 'estimateShipDateToCusto',
  ESTIMATE_SHIP_DATE_TO_DISTRI = 'estimateShipDateToDistri',
  IMEI = 'imei',
  LEASING_CONTRACT_NUMBER = 'leasingContractNumber',
  LEASING_END_DATE = 'leasingEndDate',
  LEASING_START_DATE = 'leasingStartDate',
  LEASING_TERM = 'leasingTerm',
  MAC2_ADDRESS = 'mac2Address',
  MAC_ADDRESS = 'macAddress',
  OEM_REFERENCE_NUMBER = 'oemReferenceNumber',
  PART_NUMBER = 'partNumber',
  PARENT_PRODUCT_SERIAL_NUMBER = 'parentProductSerialNumber',
  PAYMENT_PERIOD = 'paymentPeriod',
  RENTAL_END_DATE = 'rentalEndDate',
  RENTAL_EXTENSION = 'rentalExtension',
  SERIAL_NUMBER = 'serialNumber',
  TRACKING_NUMBER_TO_BP = 'trackingNumberToBP',
  TRACKING_NUMBER_TO_CUSTO = 'trackingNumberToCusto',
  TRACKING_NUMBER_TO_DISTRI = 'trackingNumberToDistri',
  USER = 'user',
  UUID = 'uuid',
  WARRANTY_ACTIVATION_NUMBER = 'warrantyActivationNumber',
  WARRANTY_EXPIRY_DATE = 'warrantyExpiryDate',
  WAN_IMEI = 'wanImei',
  UUID_HR = 'uuidHr',
  WLAN_MAC = 'wlanMac',
  WARRANTY_START_DATE = 'warrantyStartDate',
  FLASH_SYSBIOS_LEVEL = 'flashOrSysbiosLevel'
}

@Injectable()
export class AssetDataService {
  constructor(
    private httpDataService:HttpDataService,
    private ga1Service:GA1Service
  ) {
    /**/
  }

  public getAssetInfo(orderId:number):Promise<IAssetResponse> {
    return this.httpDataService.doGET(
      `rest/orderentry/orders/${orderId}/assets`,
      undefined,
      undefined,
      'Error loading asset info'
    );
  }

  public saveAssetInfo(orderId:number, data:IAsset[]):Promise<void> {
    return this.httpDataService.doPUT(
      `rest/orderentry/orders/${orderId}/assets`,
      undefined,
      data,
      undefined,
      'Error saving asset info'
    );
  }

  public calculateFields(orderId:number, data:IAssetInfoCalculation):Promise<IAssetInfoCalculation> {
    return this.httpDataService.doPOST(
      `rest/orderentry/orders/${orderId}/assets/${data.assetId}/calculate`,
      undefined,
      data,
      undefined,
      'Error calculating fields'
    );
  }

  public notifyAssetSaved = ():void => {
    this.ga1Service.sendMessage(MESSAGE_ASSET_SAVED, {});
  };

  public notifyAssetSaving = ():void => {
    this.ga1Service.sendMessage(MESSAGE_ASSET_SAVING, {});
  };

  public notifyAssetSaveFailed = (error:any):void => {
    this.ga1Service.sendMessage(MESSAGE_ASSET_SAVE_FAILED, error);
  };

  public notifyAssetValidationFailed():void {
    this.ga1Service.sendMessage(MESSAGE_ASSET_VALIDATION_FAILED, {});
  }

  public notifySOWResult(result:ISOWResult):void {
    this.ga1Service.sendMessage(MESSAGE_SOW_RESULT, result);
  }

  public get visibleColumns():IObject<AssetColumn[]> {
    return {
      G: [
        AssetColumn.PART_NUMBER,
        AssetColumn.SERIAL_NUMBER,
        AssetColumn.ASSET_TAG,
        AssetColumn.PARENT_PRODUCT_SERIAL_NUMBER,
        AssetColumn.WARRANTY_EXPIRY_DATE,
        AssetColumn.OEM_REFERENCE_NUMBER,
        AssetColumn.MAC_ADDRESS,
        AssetColumn.USER,
        AssetColumn.CATEGORY,
        AssetColumn.DESCRIPTION
      ],
      P: [
        AssetColumn.PART_NUMBER,
        AssetColumn.SERIAL_NUMBER,
        AssetColumn.ASSET_TAG,
        AssetColumn.PARENT_PRODUCT_SERIAL_NUMBER,
        AssetColumn.MAC_ADDRESS,
        AssetColumn.MAC2_ADDRESS,
        AssetColumn.UUID,
        AssetColumn.IMEI,
        AssetColumn.WAN_IMEI,
        AssetColumn.UUID_HR,
        AssetColumn.WLAN_MAC,
        AssetColumn.FLASH_SYSBIOS_LEVEL,
        AssetColumn.CATEGORY,
        AssetColumn.DESCRIPTION
      ],
      W: [
        AssetColumn.PART_NUMBER,
        AssetColumn.SERIAL_NUMBER,
        AssetColumn.WARRANTY_EXPIRY_DATE,
        AssetColumn.WARRANTY_ACTIVATION_NUMBER,
        AssetColumn.WARRANTY_START_DATE,
        AssetColumn.CATEGORY,
        AssetColumn.DESCRIPTION
      ],
      L: [
        AssetColumn.PART_NUMBER,
        AssetColumn.SERIAL_NUMBER,
        AssetColumn.LEASING_CONTRACT_NUMBER,
        AssetColumn.LEASING_START_DATE,
        AssetColumn.LEASING_TERM,
        AssetColumn.LEASING_END_DATE,
        AssetColumn.RENTAL_EXTENSION,
        AssetColumn.RENTAL_END_DATE,
        AssetColumn.PAYMENT_PERIOD,
        AssetColumn.BEGINNING_OF_PAYMENT,
        AssetColumn.END_OF_PAYMENT,
        AssetColumn.CATEGORY,
        AssetColumn.DESCRIPTION
      ],
      S: [
        AssetColumn.PART_NUMBER,
        AssetColumn.SERIAL_NUMBER,
        AssetColumn.ESTIMATE_SHIP_DATE_TO_CUSTO,
        AssetColumn.ACTUAL_SHIP_DATE_TO_CUSTO,
        AssetColumn.ESTIMATE_DELIVER_DATE_AT_CUSTO,
        AssetColumn.ACTUAL_DELIVER_DATE_AT_CUSTO,
        AssetColumn.TRACKING_NUMBER_TO_CUSTO,
        AssetColumn.ESTIMATE_SHIP_DATE_TO_BP,
        AssetColumn.ACTUAL_SHIP_DATE_TO_BP,
        AssetColumn.ESTIMATE_DELIVER_DATE_AT_BP,
        AssetColumn.ACTUAL_DELIVER_DATE_AT_BP,
        AssetColumn.TRACKING_NUMBER_TO_BP,
        AssetColumn.ESTIMATE_SHIP_DATE_TO_DISTRI,
        AssetColumn.ACTUAL_SHIP_DATE_TO_DISTRI,
        AssetColumn.ESTIMATE_DELIVER_DATE_AT_DISTRI,
        AssetColumn.ACTUAL_DELIVER_DATE_AT_DISTRI,
        AssetColumn.TRACKING_NUMBER_TO_DISTRI,
        AssetColumn.OEM_REFERENCE_NUMBER,
        AssetColumn.CATEGORY,
        AssetColumn.DESCRIPTION
      ]
    };
  }

  public get shipToBPFields():AssetColumn[] {
    return [
      AssetColumn.ESTIMATE_SHIP_DATE_TO_BP,
      AssetColumn.ACTUAL_SHIP_DATE_TO_BP,
      AssetColumn.ESTIMATE_DELIVER_DATE_AT_BP,
      AssetColumn.ACTUAL_DELIVER_DATE_AT_BP,
      AssetColumn.TRACKING_NUMBER_TO_BP
    ];
  }

  public get shipToDistriFields():AssetColumn[] {
    return [
      AssetColumn.ESTIMATE_SHIP_DATE_TO_DISTRI,
      AssetColumn.ACTUAL_SHIP_DATE_TO_DISTRI,
      AssetColumn.ESTIMATE_DELIVER_DATE_AT_DISTRI,
      AssetColumn.ACTUAL_DELIVER_DATE_AT_DISTRI,
      AssetColumn.TRACKING_NUMBER_TO_DISTRI
    ];
  }
}